import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useApi from '@/hooks/useApi';
import {
  describeClientContext,
  renderSmartPrompts,
  type SmartPromptItem,
  type SmartPromptsResponse,
} from '@/services/smartPrompts';
import type { SuggestedPrompt } from '@/components/chat/ChatLandingState';

interface UseFollowupPromptsOptions {
  /** The voter's message for the turn just completed. */
  question?: string;
  /** VoteMate's reply. What it covered is where the follow-ups come from. */
  answer?: string;
  /** Hold the request until the answer has finished streaming. */
  enabled?: boolean;
  limit?: number;
  /** Guest context, as for the landing prompts. */
  clientContext?: unknown;
  /**
   * Prompt ids already tapped in this conversation.
   *
   * The ranking reads the answer, and the answer to a tapped chip is still
   * about the same subject — so without this the same chip wins again every
   * turn and the row never moves on.
   */
  askedPromptIds?: string[];
}

/**
 * The prompts offered after VoteMate answers a question.
 *
 * Subjects come from matching the answer against the voter's own ballot on the
 * backend, so a follow-up always names a race or measure they can actually
 * vote on. Nothing here is model-written, so this costs no model call and adds
 * nothing to the turn's latency — it runs after the answer is already on
 * screen.
 *
 * Returns `null` on any failure, which reads to the caller as "no follow-ups
 * for this turn". The chat then looks exactly as it did before follow-ups
 * existed, rather than showing an error.
 *
 * Mirrors `votemate-mobile-app/src/hooks/useFollowupPrompts.ts`.
 */
export default function useFollowupPrompts({
  question,
  answer,
  enabled = true,
  limit = 3,
  clientContext,
  askedPromptIds,
}: UseFollowupPromptsOptions): SuggestedPrompt[] | null {
  const { t, i18n } = useTranslation('chat');
  const query = useApi();
  const language = i18n.language;

  // The result is stored with the turn it belongs to, so a new turn reads as
  // "no follow-ups yet" without an effect having to clear it first — the
  // previous turn's chips never linger under a newer answer.
  const [result, setResult] = useState<{ key: string; items: SmartPromptItem[] | null } | null>(
    null
  );

  const contextKey = describeClientContext(clientContext);
  const askedKey = (askedPromptIds ?? []).join(',');
  // One request per completed turn. The answer is clipped to keep the key
  // small, so its full length and the question ride along too — two answers
  // sharing a 200-character prefix would otherwise collide and the second
  // turn would silently reuse the first turn's follow-ups.
  const turnKey = [
    language,
    askedKey,
    (answer ?? '').length,
    (answer ?? '').slice(0, 200),
    (question ?? '').slice(0, 100),
  ].join('|');

  useEffect(() => {
    if (!enabled || !answer) return undefined;

    let cancelled = false;

    const load = async () => {
      try {
        const response = await query('/api/prompts/suggested', {
          method: 'POST',
          body: JSON.stringify({
            mode: 'followup',
            locale: language,
            limit,
            client_context: clientContext ?? undefined,
            asked_prompt_ids: askedPromptIds ?? [],
            turn: { question, answer },
          }),
          headers: { 'Content-Type': 'application/json' },
        });
        const data: SmartPromptsResponse = await response.json();
        if (!cancelled) {
          setResult({ key: turnKey, items: Array.isArray(data?.prompts) ? data.prompts : null });
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('Follow-up prompts fetch failed; showing none:', err);
        setResult({ key: turnKey, items: null });
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, turnKey, limit, contextKey]);

  const items = result && result.key === turnKey ? result.items : null;

  return useMemo(() => {
    if (!items) return null;
    const rendered = renderSmartPrompts(items, t, i18n);
    return rendered.length > 0 ? rendered : null;
  }, [items, t, i18n]);
}
